import { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Flag, ArrowRight, User, Users } from 'react-feather';
import { DisputeRoutingRule, DisputeRoutingRuleCreate } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getSupplierDisputeRoutingRules,
  createDisputeRoutingRule,
  updateDisputeRoutingRule,
  deleteDisputeRoutingRule,
} from '../../../../redux/services/disputeRoutingRuleService';
import { getSupplierSummary } from '../../../../redux/services/suppliersService';
import { DillButton, DillToggle } from '../../../general/Dill';
import { DisputeRoutingRuleFormModal } from './DisputeRoutingRuleFormModal';

interface DisputeRoutingRulesTabProps {
  supplierId: string;
}

export const DisputeRoutingRulesTab = ({ supplierId }: DisputeRoutingRulesTabProps) => {
  const dispatch = useAppDispatch();
  const { rules, loadingList } = useAppSelector((state) => state.disputeRoutingRules);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { supplierUsers } = useAppSelector((state) => state.supplier);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<DisputeRoutingRule | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (supplierId) {
      dispatch(getSupplierDisputeRoutingRules({ supplierId }));
    }
  }, [supplierId, dispatch]);

  useEffect(() => {
    if (supplierId && supplierUsers.length === 0) {
      dispatch(getSupplierSummary({ supplierId }));
    }
  }, [supplierId, supplierUsers.length, dispatch]);

  const isLoading = loadingList.includes('getSupplierDisputeRoutingRules');

  // Reasons a dispute can carry are configured per supplier and shared with the buyer AR
  // portal's shortpay flow — a routing rule targets one of those exact strings. "Tax Exemption"
  // and "Pricing" are always offered here even if a supplier hasn't added them to their own
  // reason list yet, since they're the two categories this feature is built around.
  const configuredReasons = currentSupplier?.partialPaymentReasons || [];
  const categoryOptions = Array.from(new Set([...configuredReasons])).map((reason) => ({
    label: reason,
    value: reason,
  }));

  const describeAssignees = (rule: DisputeRoutingRule): string => {
    const names = (rule.assignToUserIds || [])
      .map((userId) => {
        const match = supplierUsers.find((u) => u.id === userId);
        return match ? [match.firstName, match.lastName].filter(Boolean).join(' ') : null;
      })
      .filter(Boolean) as string[];

    if (rule.assigneeMode === 'CUSTOMER_SALES_REP') {
      return names.length > 0
        ? `the customer's sales rep, or ${names.join(', ')} if none is found`
        : "the customer's sales rep";
    }
    if (rule.assigneeMode === 'TAX_EXEMPTION_TEAM') {
      return names.length > 0
        ? `the Tax Exemption Team, or ${names.join(', ')} if no team member is found`
        : 'the Tax Exemption Team';
    }
    return names.length > 0 ? names.join(', ') : 'nobody yet — pick at least one person';
  };

  const handleCreate = () => {
    setEditingRule(null);
    setErrorMessage(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (rule: DisputeRoutingRule) => {
    setEditingRule(rule);
    setErrorMessage(null);
    setIsFormModalOpen(true);
  };

  const handleClose = () => {
    setIsFormModalOpen(false);
    setEditingRule(null);
    setErrorMessage(null);
  };

  const extractErrorMessage = (payload: unknown): string | null => {
    if (payload && typeof payload === 'object' && 'errors' in (payload as any)) {
      const errors = (payload as any).errors;
      if (Array.isArray(errors) && errors[0]?.msg) return errors[0].msg as string;
    }
    return null;
  };

  const handleSubmit = async (data: DisputeRoutingRuleCreate) => {
    if (!supplierId) return;
    setErrorMessage(null);
    const result = editingRule
      ? await dispatch(updateDisputeRoutingRule({ supplierId, ruleId: editingRule.id, data }))
      : await dispatch(createDisputeRoutingRule({ supplierId, data: { ...data, supplierId } }));
    const payload = result.payload as { success?: boolean } | null;
    if (!payload || payload.success === false) {
      setErrorMessage(extractErrorMessage(payload) || 'Failed to save routing rule.');
      return;
    }
    handleClose();
    dispatch(getSupplierDisputeRoutingRules({ supplierId }));
  };

  const handleToggleActive = async (rule: DisputeRoutingRule) => {
    await dispatch(
      updateDisputeRoutingRule({
        supplierId,
        ruleId: rule.id,
        data: { isActive: !rule.isActive },
      })
    );
    dispatch(getSupplierDisputeRoutingRules({ supplierId }));
  };

  const handleDelete = async (ruleId: string) => {
    await dispatch(deleteDisputeRoutingRule({ supplierId, ruleId }));
    dispatch(getSupplierDisputeRoutingRules({ supplierId }));
  };

  return (
    <div className="w-full h-full pt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
            <Flag size={18} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 m-0">Dispute Routing</p>
            <p className="text-xs text-gray-400 mt-0.5 m-0">
              Automatically creates a review task for the right person whenever a dispute with a
              given category is filed — by a customer's short payment, or created manually.
            </p>
          </div>
        </div>
        <DillButton
          label="New rule"
          icon={<Plus size={14} />}
          onClick={handleCreate}
          isDisabled={categoryOptions.length === 0}
          height={31}
        />
      </div>

      {categoryOptions.length === 0 && (
        <p className="text-xs text-gray-400 mb-3">
          Set up dispute reasons under your dispute settings before creating a routing rule.
        </p>
      )}

      {isLoading && rules.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-8">Loading dispute routing rules…</p>
      )}

      {!isLoading && rules.length === 0 && (
        <div className="bg-white border border-dashed border-gray-200 rounded-xl py-10 flex flex-col items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-300 flex items-center justify-center">
            <Flag size={18} />
          </div>
          <p className="text-sm text-gray-500 m-0 mt-1">No dispute routing rules yet</p>
          <p className="text-xs text-gray-400 m-0">
            e.g. route "Tax Exemption" disputes to your tax team, or "Pricing" disputes to the
            customer's sales rep.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        {rules.map((rule) => (
          <div
            key={rule.id}
            className="group bg-white border border-gray-100 rounded-xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-amber-50 text-amber-600">
              {rule.assigneeMode === 'CUSTOMER_SALES_REP' ? (
                <User size={18} />
              ) : (
                <Users size={18} />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-semibold text-gray-900 m-0 truncate">
                  {rule.name || rule.disputeCategory}
                </p>
                <span className="text-[10px] font-medium text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 shrink-0">
                  {rule.disputeCategory}
                </span>
                {rule.priority === 'HIGH' && (
                  <span className="text-[10px] font-medium text-red-600 bg-red-50 rounded-full px-2 py-0.5 shrink-0">
                    High priority
                  </span>
                )}
                {!rule.isActive && (
                  <span className="text-[10px] font-medium text-gray-400 bg-gray-100 rounded-full px-2 py-0.5 shrink-0">
                    Paused
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 m-0 flex items-center gap-1 truncate">
                Task assigned to
                <ArrowRight size={11} className="text-gray-300 shrink-0" />
                <span className="font-medium text-gray-700 truncate">
                  {describeAssignees(rule)}
                </span>
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0 pl-3 border-l border-gray-100">
              <DillToggle value={rule.isActive} onChange={() => handleToggleActive(rule)} />
              <DillButton
                variant="icon-default"
                icon={<Edit2 size={12} />}
                onClick={() => handleEdit(rule)}
              />
              <DillButton
                variant="icon-red"
                icon={<Trash2 size={12} />}
                onClick={() => handleDelete(rule.id)}
              />
            </div>
          </div>
        ))}
      </div>

      <DisputeRoutingRuleFormModal
        open={isFormModalOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        initialData={editingRule}
        isLoading={
          loadingList.includes('createDisputeRoutingRule') ||
          loadingList.includes('updateDisputeRoutingRule')
        }
        errorMessage={errorMessage}
        categoryOptions={categoryOptions}
        supplierUsers={supplierUsers}
      />
    </div>
  );
};

export default DisputeRoutingRulesTab;
